// Catalog.ID Client — Registry Ceremony Mode Handlers (derived-authority signing)
// ============================================================================
// The browser half of the A8b keybag hop. CPR's client-service asks this tab to
// custody an ACCOUNT AUTHORITY key and to sign account-stream ceremonies with
// it; the private key never leaves this module, and CPR never sees it.
//
// Trust chain for an inbound envelope, in order:
//   1. cld-id's relay verified the module signature over the canonical bytes
//      (`KeyGrantRelay.TryRejectUnverifiedRegistryRequestAsync`). Browser code
//      does not re-verify.
//   2. `dispatchRegistryRequest` pinned `_senderServiceId === requesterId ===
//      'cpr'`. Only the registry module may ask for these modes; CPR-8.9's
//      broader vault-endpoint allowlist is recorded as follow-up, not built.
//   3. HERE: identity context, claim-type whitelist, chain-digest recheck, key
//      resolution, and ONE structured consent — in that order, so that a
//      refusal on a bad chain happens before any key material is touched and
//      before the user is asked anything.
//
// The ordering in step 3 is deliberate and load-bearing. Consent-then-check
// would train the user to approve dialogs that get refused anyway; check-then-
// consent means every dialog shown is one that will actually execute.
//
// ── WHERE THE AUTHORITY KEY COMES FROM (cid-encrypted-storage §5.2, M5) ─────
// It is DERIVED, not stored. Until M5 each generation was a random hybrid seed
// in the key bag under `registry_authority@n`, which cost a supersecret prompt
// per mint and — because device copies are only written at enrollment — forced
// every OTHER device of the account into a supersecret unlock at its next
// login (§3.4's hidden second cost). Now:
//
//     seed64 = deriveModuleKey(derive_root, "cid", "registry-authority:v1:<n>", 64)
//
// and the account carries only the `cid/registry-authority` blob saying which
// generations exist, which one is active, and what fingerprint each must
// produce (`crypto/registry-authority-state.ts`). The seed is derived inside
// the handler that needs it and zeroed in a `finally`; nothing keeps a copy.
//
// The rev-1 worry that a generated key could be stranded between mint and
// acceptance is gone twice over: the state blob is written BEFORE the answer
// (so a closed tab still leaves a document naming the key), and a derived key
// can always be re-derived from the root on any unlocked device.
//
// Every listed generation — retired ones included — is derived and offered,
// because after a rotation the OLD key still owns the estate and still signs
// the rebinds and the `authority_rotate` proof chain (asset-ownership D9).
// ============================================================================

import type { Shell } from '@catalog-shell';
import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import {
    signHybrid, wrapperToCombinedBytes, signingPublicKeyFromSeed,
} from '../crypto/signing';
import { sha512 } from '@noble/hashes/sha2.js';
import { DERIVE_ROOT_KEY_ID, DERIVE_ROOT_BYTES } from '../crypto/derive';
import {
    REGISTRY_AUTHORITY_BLOB, appendGeneration, decodeAuthorityState,
    deriveAuthoritySeed, emptyAuthorityState, nextAuthorityIndex,
    type AuthorityGenerationStatus, type RegistryAuthorityState,
} from '../crypto/registry-authority-state';
import { readCidBlob, writeCidBlob } from '../state/cid-blobs';
import { BlobConflictError } from '../api/blobs';
import {
    checkCeremonyClaimAllowed, ceremonyClaimCanonicalBytes,
    authorityRotateProofBytes, computeCeremonyClaimsDigest,
    summarizeCeremonyClaim, toHexLower,
    type CeremonyClaimSummary,
} from './ceremony-claims';
import { showCeremonyConsent } from './ceremony-consent-modal';
import { CEREMONY_IDENTITY_KIND_PERSONAL } from './ceremony-types';
import type {
    CeremonyAuthorityKeyWire, CeremonyEnrolledStreamWire, CeremonySignedClaimWire,
} from './ceremony-types';
import type { RegistryRequest, RegistryResponse } from './registry-types';
import { ensureUnlocked } from './unlock-gate';

/**
 * Cap on the length of one ceremony chain. Generous for every real ceremony
 * (a mixed-estate rotation is a rotate + N grants + a successor + a handful of
 * rebinds/revokes) and small enough that a hostile or buggy requester cannot
 * turn one consent into an unbounded amount of signing. ML-DSA-65 signing is
 * ~10ms, so 32 keeps the dialog-to-done latency inside a second.
 */
const MAX_CEREMONY_CLAIMS = 32;

/**
 * How many times `ceremony-generate-authority-key` re-applies its append after
 * losing the compare-and-set. ONE retry, not a loop: a second device minting an
 * authority key at the same moment is the case worth surviving, and a document
 * that keeps moving under us means something is writing in a loop — spinning
 * would turn that into a hung ceremony console.
 */
const GENERATE_ATTEMPTS = 2;

type ResponseBase = Pick<RegistryResponse, 'type' | 'to' | 'requestId'>;

/** One authority generation, derived from the account's root. */
interface AuthorityKey {
    readonly fingerprint: string;
    readonly publicKey: Uint8Array;
    /** OWNED by the resolver's caller — zeroed by {@link zeroAuthorityKeys}. */
    readonly secretSeed: Uint8Array;
    readonly keyIndex: number;
    readonly status: AuthorityGenerationStatus;
}

/**
 * A refusal raised while resolving the account's authority keys.
 *
 * `reason` is what crosses the module boundary to CPR and is matched on there;
 * `detail` is for a developer reading the thrown error and never travels.
 */
class AuthorityStateError extends Error {
    constructor(public readonly reason: string, detail?: string) {
        super(detail ? `${reason}: ${detail}` : reason);
        this.name = 'AuthorityStateError';
    }
}

export interface CeremonyDispatchDeps {
    shell: Shell;
    getActiveCid: () => string | null;
    /** Test seam; production uses the shared
     *  `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;
    /**
     * The account's 32-byte derive root, or null when the bag holds none.
     * Defaults to the in-memory bag; injected by tests so the derivation runs
     * against a FIXED root and its outputs can be pinned.
     */
    getDeriveRoot?: () => Uint8Array | null;
    /**
     * Read the `cid/registry-authority` document. Null ⇒ the account has no
     * authority key yet, which is a normal answer, not an error.
     * Defaults to {@link readCidBlob}.
     */
    readAuthorityState?: () => Promise<{ value: unknown; generation: number } | null>;
    /**
     * Write it back, compare-and-set. Defaults to {@link writeCidBlob}; throws
     * {@link BlobConflictError} when another device got there first.
     */
    writeAuthorityState?: (
        state: RegistryAuthorityState, ifGeneration: number,
    ) => Promise<{ generation: number }>;
    /** Read the off-ledger enrollment records (CID claims). Absent ⇒ omitted. */
    listEnrollmentRecords?: () => Promise<CeremonyEnrolledStreamWire[]>;
    /** Write one enrollment record. Absent ⇒ `enrollment-store-unavailable`. */
    saveEnrollmentRecord?: (record: CeremonyEnrolledStreamWire) => Promise<string>;
}

/**
 * Route a ceremony-mode envelope. Called from `dispatchRegistryRequest`, which
 * has already applied the sender and requester pins.
 */
export async function dispatchCeremonyRequest(
    request: RegistryRequest,
    deps: CeremonyDispatchDeps,
): Promise<RegistryResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    // Defence in depth: `dispatchRegistryRequest` checks these too, but this
    // entry point is exported and a future caller must not be able to skip them.
    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return unavailable(base, 'sender-mismatch');
    }
    if (request.requesterId !== 'cpr') {
        return unavailable(base, 'unsupported-requester');
    }

    const cid = deps.getActiveCid();
    if (!cid) return unavailable(base, 'no-active-identity');

    // The identity context is REQUIRED and closed. An absent context would
    // have to be defaulted, and the only sane default (personal) is exactly
    // the wrong answer the day an org ceremony arrives from an older caller.
    const contextKind = request.identityContext?.kind;
    if (typeof contextKind !== 'string' || contextKind.length === 0) {
        return unavailable(base, 'identity-context-required');
    }
    if (contextKind !== CEREMONY_IDENTITY_KIND_PERSONAL) {
        return unavailable(base, 'identity-context-unsupported');
    }

    switch (request.mode) {
        case 'ceremony-authority-status':
            return await handleAuthorityStatus(request, base, deps);
        case 'ceremony-generate-authority-key':
            return await handleGenerateAuthorityKey(request, base, deps);
        case 'sign-ceremony-claims':
            return await handleSignCeremonyClaims(request, base, deps, cid);
        case 'ceremony-commit':
            return await handleCommit(request, base, deps);
        default:
            return unavailable(base, 'unsupported-mode');
    }
}

// ── ceremony-authority-status ───────────────────────────────────────────
//
// Read-only disclosure of PUBLIC key material plus the identity's recorded
// enrollments. No consent: the same posture `get-active-identity` takes, and
// for the same reason — everything disclosed is already public or is the
// caller's own bookkeeping. It does need an unlocked tab, because the public
// halves are derived from the account's root rather than served by the account
// public-key surface (an authority key is deliberately NOT an
// `accountpublickey` row — it is CPR-facing, not CID-facing).
//
// The answer shape is the M5 wire contract with CPR and did not change when
// the key stopped being a bag entry: `authorityKeys[{fingerprint, publicKey,
// keyIndex}]` + `activeAuthorityFingerprint` (+ `enrolledStreams`).
// `keyIndex` is now the generation index from the document, which is what it
// always meant.
async function handleAuthorityStatus(
    request: RegistryRequest,
    base: ResponseBase,
    deps: CeremonyDispatchDeps,
): Promise<RegistryResponse> {
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    let authorityKeys: AuthorityKey[];
    try {
        authorityKeys = await resolveAuthorityKeys(deps);
    } catch (err) {
        return unavailable(base, authorityRefusal(err));
    }

    try {
        const wire: CeremonyAuthorityKeyWire[] = authorityKeys.map(k => ({
            fingerprint: k.fingerprint,
            publicKey: aes.toBase64(k.publicKey),
            keyIndex: k.keyIndex,
        }));

        // From the recorded STATUS, never from the ordering: a document whose
        // tip was retired by a repair must not have that tip handed back as
        // the fingerprint the next ceremony pins.
        const active = authorityKeys.find(k => k.status === 'active') ?? null;

        const response: RegistryResponse = {
            ...base,
            status: 'approved',
            authorityKeys: wire,
            activeAuthorityFingerprint: active?.fingerprint ?? null,
        };

        if (deps.listEnrollmentRecords) {
            try {
                response.enrolledStreams = await deps.listEnrollmentRecords();
            } catch {
                // A failed read must not turn a successful key disclosure into
                // an error: CPR's own vault record is the authoritative
                // enrollment cache, and this list is a cross-machine
                // convenience.
                response.enrolledStreams = [];
            }
        }
        return response;
    } finally {
        zeroAuthorityKeys(authorityKeys);
    }
}

// ── ceremony-generate-authority-key ─────────────────────────────────────
//
// "Generate" is now a bookkeeping step: the key for generation n is already a
// function of the account's root, so what this mode does is decide WHICH n,
// record it, and hand back the public half.
//
// The state blob is written BEFORE the answer. That ordering is the whole
// point: an `authority_rotate` body carries the incoming public key inside its
// signed bytes, so CPR cannot build the claim until this returns, and a
// document that never landed would leave the account unable to say which
// generation is current. A derived key itself can never be lost (that was the
// rev-1 worry, and it is gone) — the DOCUMENT is what has to be durable.
//
// No supersecret and no dialog. There is no bag mutation left to gate, and a
// dialog here would be consent theatre ahead of the ONE that matters, on the
// signing step: an authority key that is generated but never named in a claim
// does nothing at all.
async function handleGenerateAuthorityKey(
    request: RegistryRequest,
    base: ResponseBase,
    deps: CeremonyDispatchDeps,
): Promise<RegistryResponse> {
    const unlocked = await ensureUnlocked(request, deps);
    if (!unlocked) return unavailable(base, 'cid-locked');

    let root: Uint8Array;
    try {
        root = requireDeriveRoot(deps);
    } catch (err) {
        return unavailable(base, authorityRefusal(err));
    }

    for (let attempt = 0; attempt < GENERATE_ATTEMPTS; attempt++) {
        let current: { state: RegistryAuthorityState; generation: number };
        try {
            current = await readAuthorityState(deps);
        } catch (err) {
            return unavailable(base, authorityRefusal(err));
        }

        const index = nextAuthorityIndex(current.state);
        const seed = deriveAuthoritySeed(root, index);
        let publicKey: Uint8Array;
        try {
            publicKey = signingPublicKeyFromSeed(seed);
        } finally {
            // The seed's only job here was to produce the public half; the
            // signing step re-derives it when a claim actually needs it.
            seed.fill(0);
        }
        const fingerprint = toHexLower(sha512(publicKey));

        const next = appendGeneration(current.state, {
            index, fingerprint, createdAt: new Date().toISOString(),
        });

        try {
            await writeAuthorityState(deps, next, current.generation);
        } catch (err) {
            // A lost compare-and-set means another device minted a generation
            // between our read and our write. Re-read and re-apply: the new
            // index is recomputed against what actually landed, so the rival's
            // generation survives instead of being clobbered.
            if (err instanceof BlobConflictError && attempt + 1 < GENERATE_ATTEMPTS) continue;
            return unavailable(base, 'authority-key-persist-failed:' + shortError(err));
        }

        // `keyLabel` is display-only on CPR's side and has nowhere to live now
        // that there is no bag entry to label; it is accepted and ignored
        // rather than refused, so the wire shape is unchanged.
        void request.keyLabel;

        return {
            ...base,
            status: 'approved',
            generatedFingerprint: fingerprint,
            generatedPublicKey: aes.toBase64(publicKey),
            generatedKeyIndex: index,
        };
    }

    /* c8 ignore next */
    return unavailable(base, 'authority-key-persist-failed:conflict');
}

// ── sign-ceremony-claims ────────────────────────────────────────────────

async function handleSignCeremonyClaims(
    request: RegistryRequest,
    base: ResponseBase,
    deps: CeremonyDispatchDeps,
    cid: string,
): Promise<RegistryResponse> {
    const claims = request.claims;
    if (!Array.isArray(claims) || claims.length === 0) {
        return unavailable(base, 'no-claims');
    }
    if (claims.length > MAX_CEREMONY_CLAIMS) {
        return unavailable(base, 'too-many-claims');
    }
    if (typeof request.expectedAuthorityFp !== 'string' || request.expectedAuthorityFp.length === 0) {
        return unavailable(base, 'expected-authority-required');
    }

    // ── Gate 1: the claim-type whitelist, BEFORE anything else.
    // The whole two-tier anti-theft model rests on this. A refusal names the
    // index so a chain builder can find the offending claim without guessing.
    const summaries: CeremonyClaimSummary[] = [];
    for (let i = 0; i < claims.length; i++) {
        const plan = claims[i]!;
        if (!plan || typeof plan !== 'object') {
            return unavailable(base, `malformed-claim-plan:${i}`);
        }
        const verdict = checkCeremonyClaimAllowed(plan.body);
        if (!verdict.ok) {
            return unavailable(base, `claim-not-permitted:${i}:${verdict.reason}`);
        }
        if (typeof plan.signerFingerprint !== 'string' || !/^[0-9a-f]{128}$/.test(plan.signerFingerprint)) {
            return unavailable(base, `malformed-signer-fingerprint:${i}`);
        }
        const isRotation = verdict.claimType === 'authority_rotate';
        if (isRotation && typeof plan.proofSignerFingerprint !== 'string') {
            // Q-A4-1: the proof is REQUIRED. Signing a rotation without one
            // would produce a claim the operator rejects, after spending the
            // cold key — and after asking the user to approve it.
            return unavailable(base, `rotation-proof-signer-required:${i}`);
        }
        if (!isRotation && plan.proofSignerFingerprint !== undefined) {
            return unavailable(base, `proof-signer-not-applicable:${i}`);
        }
        if ('new_authority_proof' in plan.body) {
            return unavailable(base, `claim-carries-proof:${i}`);
        }
        summaries.push(summarizeCeremonyClaim(plan.body));
    }

    // ── Gate 2: chain-digest recheck.
    // The relay's Ed25519 signature commits to `claimsDigestBase64`, not to the
    // body. Recomputing it here is what stops a verified envelope from being
    // paired with a substituted chain — the same role gates 11/12 play in the
    // EFS decap handler.
    const recomputed = computeCeremonyClaimsDigest(claims.map(c => ({
        signerFingerprint: c.signerFingerprint,
        ...(c.proofSignerFingerprint !== undefined
            ? { proofSignerFingerprint: c.proofSignerFingerprint } : {}),
        body: c.body,
    })));
    if (recomputed !== request.claimsDigestBase64) {
        return unavailable(base, 'claims-digest-mismatch');
    }

    // ── Gate 3: unlock, then resolve every key the chain names.
    const unlocked = await ensureUnlocked(request, deps);
    if (!unlocked) return unavailable(base, 'cid-locked');

    let authorityKeys: AuthorityKey[];
    try {
        authorityKeys = await resolveAuthorityKeys(deps);
    } catch (err) {
        // `authority-state-corrupt` lands here: the derived key disagrees with
        // the fingerprint the document records, so we do NOT know which key
        // this chain would be signed with. Refuse; never sign.
        return unavailable(base, authorityRefusal(err));
    }

    try {
        const byFingerprint = new Map(authorityKeys.map(k => [k.fingerprint, k] as const));

        // The requester's pin. A machine talking to the wrong Catalog.ID
        // account fails HERE, with a name for the problem, instead of producing
        // a perfectly valid signature by an authority nobody expected.
        if (!byFingerprint.has(request.expectedAuthorityFp)) {
            return unavailable(base, 'authority-key-not-in-bag');
        }

        for (let i = 0; i < claims.length; i++) {
            const plan = claims[i]!;
            if (!byFingerprint.has(plan.signerFingerprint)) {
                return unavailable(base, `signer-key-not-in-bag:${i}`);
            }
            if (plan.proofSignerFingerprint !== undefined) {
                const proofKey = byFingerprint.get(plan.proofSignerFingerprint);
                if (!proofKey) return unavailable(base, `proof-signer-key-not-in-bag:${i}`);
                // The proof exists to prove possession of the key being
                // INSTALLED. A proof by any other key proves nothing, so the
                // two must agree before the user is asked to approve the
                // rotation.
                const named = typeof plan.body['new_authority_key'] === 'string'
                    ? plan.body['new_authority_key'] as string
                    : '';
                if (named !== aes.toBase64(proofKey.publicKey)) {
                    return unavailable(base, `rotation-proof-signer-mismatch:${i}`);
                }
            }
        }

        // ── Gate 4: ONE consent for the whole chain, rendering semantics.
        const decision = await showCeremonyConsent(deps.shell, {
            owningCid: cid,
            requesterId: request.requesterId,
            ceremonyKind: request.ceremonyKind ?? 'grant',
            ceremonyLabel: request.ceremonyLabel,
            expectedAuthorityFp: request.expectedAuthorityFp,
            summaries,
            signerFingerprints: claims.map(c => c.signerFingerprint),
        });
        if (decision !== 'approve') {
            return { ...base, status: 'denied', reason: 'user-denied' };
        }

        // ── Sign.
        const signed: CeremonySignedClaimWire[] = [];
        for (const plan of claims) {
            const signer = byFingerprint.get(plan.signerFingerprint)!;
            let finalBody: Record<string, unknown> = plan.body;

            if (plan.proofSignerFingerprint !== undefined) {
                const proofKey = byFingerprint.get(plan.proofSignerFingerprint)!;
                const proofBytes = authorityRotateProofBytes(plan.body);
                const proof = wrapperToCombinedBytes(signHybrid(proofBytes, proofKey.secretSeed));
                // Sign-then-embed: the proof covers the body WITHOUT itself,
                // and the outer signature then covers the body WITH it.
                finalBody = { ...plan.body, new_authority_proof: aes.toBase64(proof) };
            }

            const canonical = ceremonyClaimCanonicalBytes(finalBody);
            const signature = wrapperToCombinedBytes(signHybrid(canonical, signer.secretSeed));
            signed.push({
                claim: finalBody,
                canonicalBytes: aes.toBase64(canonical),
                claimDigest: toHexLower(sha512(canonical)),
                signerFingerprint: signer.fingerprint,
                publicKey: aes.toBase64(signer.publicKey),
                signature: aes.toBase64(signature),
            });
        }

        return { ...base, status: 'approved', signedClaims: signed };
    } finally {
        zeroAuthorityKeys(authorityKeys);
    }
}

// ── ceremony-commit ─────────────────────────────────────────────────────
//
// Called AFTER the operator accepted the chain. Records "this identity is
// enrolled in stream X at operator Y under authority Z" as an off-ledger CID
// claim, so a second machine belonging to the same person can discover the
// stream without being told. CPR's own vault prefs hold the same facts and are
// what the local owned-flag reads; this copy exists purely for discovery.
async function handleCommit(
    request: RegistryRequest,
    base: ResponseBase,
    deps: CeremonyDispatchDeps,
): Promise<RegistryResponse> {
    if (!deps.saveEnrollmentRecord) return unavailable(base, 'enrollment-store-unavailable');

    const streamGenesisId = request.streamGenesisId;
    const operatorId = request.streamOperatorId;
    const handle = request.streamHandle;
    const authorityFingerprint = request.authorityFingerprint;
    if (!streamGenesisId || !operatorId || !handle || !authorityFingerprint) {
        return unavailable(base, 'missing-required-field');
    }
    if (!/^[0-9a-f]{32}$/.test(handle)) return unavailable(base, 'malformed-handle');
    if (!/^[0-9a-f]{128}$/.test(authorityFingerprint)) {
        return unavailable(base, 'malformed-authority-fingerprint');
    }

    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    try {
        const claimUuid = await deps.saveEnrollmentRecord({
            streamGenesisId, operatorId, handle, authorityFingerprint,
            updatedAt: new Date().toISOString(),
        });
        return { ...base, status: 'approved', enrollmentClaimUuid: claimUuid };
    } catch (err) {
        return unavailable(base, 'enrollment-record-failed:' + shortError(err));
    }
}

// ── helpers ─────────────────────────────────────────────────────────────

/**
 * The account's derive root, or a named refusal.
 *
 * `derive-root-missing` is reachable only for an account created before
 * cid-encrypted-storage §5.1 landed — `finalizeCredentials` mints the root at
 * bag creation now, and a dev account older than that is recreated rather than
 * migrated. It is a REFUSAL and not a mint: minting a root here would produce
 * an authority key that no other device of the account could reproduce.
 */
function requireDeriveRoot(deps: CeremonyDispatchDeps): Uint8Array {
    const root = (deps.getDeriveRoot ?? inMemoryDeriveRoot)();
    if (root === null) throw new AuthorityStateError('derive-root-missing');
    if (root.length !== DERIVE_ROOT_BYTES) {
        throw new AuthorityStateError('derive-root-malformed');
    }
    return root;
}

function inMemoryDeriveRoot(): Uint8Array | null {
    const rootB64 = keys.getPrivateKey(DERIVE_ROOT_KEY_ID);
    if (rootB64 === null) return null;
    try {
        return aes.fromBase64(rootB64);
    } catch {
        // Undecodable is not absent: the length check in requireDeriveRoot
        // turns this into `derive-root-malformed`, which says what is wrong.
        return new Uint8Array(0);
    }
}

/** The `cid/registry-authority` document, decoded; empty when there is none. */
async function readAuthorityState(
    deps: CeremonyDispatchDeps,
): Promise<{ state: RegistryAuthorityState; generation: number }> {
    const read = deps.readAuthorityState
        ?? (() => readCidBlob<unknown>(REGISTRY_AUTHORITY_BLOB));
    const row = await read();
    if (row === null) return { state: emptyAuthorityState(), generation: 0 };
    let state: RegistryAuthorityState;
    try {
        state = decodeAuthorityState(row.value);
    } catch (err) {
        // NOT "assume there are no generations": that would mint a second
        // generation 0 over the top of a document we merely failed to read.
        throw new AuthorityStateError('authority-state-corrupt', shortError(err));
    }
    return { state, generation: row.generation };
}

async function writeAuthorityState(
    deps: CeremonyDispatchDeps,
    state: RegistryAuthorityState,
    ifGeneration: number,
): Promise<{ generation: number }> {
    const write = deps.writeAuthorityState
        ?? ((next: RegistryAuthorityState, generation: number) =>
            writeCidBlob(REGISTRY_AUTHORITY_BLOB, next, generation));
    return await write(state, ifGeneration);
}

/**
 * Every generation the account records, DERIVED from its root, ascending.
 *
 * Retired generations are included: after a rotation the old key is still the
 * effective owner of every key-owned asset and is still the `proofSigner` an
 * `authority_rotate` chain names (D9).
 *
 * The stored fingerprint is verified against the derived public key for every
 * one of them. A mismatch means the root or the document is not this account's
 * — a bundle restored from elsewhere, a half-finished root rotation — so the
 * whole resolution is refused rather than partially trusted. Unlike the bag
 * version this does NOT skip the bad entry: with the key derived rather than
 * stored, one mismatch is evidence about the ROOT, and the others are no more
 * trustworthy than the one that failed.
 *
 * The caller owns the returned seeds and must {@link zeroAuthorityKeys} them.
 */
async function resolveAuthorityKeys(deps: CeremonyDispatchDeps): Promise<AuthorityKey[]> {
    const root = requireDeriveRoot(deps);
    const { state } = await readAuthorityState(deps);

    const out: AuthorityKey[] = [];
    try {
        for (const generation of state.generations) {
            const secretSeed = deriveAuthoritySeed(root, generation.index);
            let publicKey: Uint8Array;
            try {
                publicKey = signingPublicKeyFromSeed(secretSeed);
            } catch (err) {
                secretSeed.fill(0);
                throw new AuthorityStateError('authority-state-corrupt',
                    `generation ${generation.index} does not expand (${shortError(err)})`);
            }
            const fingerprint = toHexLower(sha512(publicKey));
            if (fingerprint !== generation.fingerprint) {
                secretSeed.fill(0);
                throw new AuthorityStateError('authority-state-corrupt',
                    `generation ${generation.index} derives a different key `
                    + 'than the document records');
            }
            out.push({
                fingerprint,
                publicKey,
                secretSeed,
                keyIndex: generation.index,
                status: generation.status,
            });
        }
    } catch (err) {
        zeroAuthorityKeys(out);
        throw err;
    }
    return out;
}

/** §4.1's invariant: a derived secret lives for one call and no longer. */
function zeroAuthorityKeys(authorityKeys: readonly AuthorityKey[]): void {
    for (const key of authorityKeys) key.secretSeed.fill(0);
}

/** The refusal reason for a failure raised while resolving authority keys. */
function authorityRefusal(err: unknown): string {
    if (err instanceof AuthorityStateError) return err.reason;
    const message = err instanceof Error ? err.message : '';
    // Raised by `state/cid-blobs.ts` when the default deps are in play.
    if (message === 'cid-locked' || message === 'derive-root-missing'
        || message === 'derive-root-malformed') {
        return message;
    }
    return 'authority-state-read-failed:' + shortError(err);
}

function unavailable(base: ResponseBase, reason: string): RegistryResponse {
    // `before-persist` on every refusal here: none of these paths mutate CID
    // state, and a ceremony that was refused before signing definitely did not
    // reach the operator either.
    return { ...base, status: 'unavailable', reason, phase: 'before-persist' };
}

function shortError(err: unknown): string {
    return err instanceof Error ? err.message.slice(0, 80) : 'unknown';
}
